import type {
  TColumnType,
  TSaveColumnState
} from "components/column-config-views";
import type { CsiSystem } from "design-scope";

/**
 * Synthetic `columnList` row: persists List/Hierarchy (+ UF/MF) selection inside
 * `design_scope_column_config` / saved-view `column_config` without a GraphQL
 * schema field. AG Grid never sees a matching colId, so it has no grid effect.
 */
export const DESIGN_SCOPE_VIEW_META_FIELD_NAME =
  "__ciq_design_scope_view_meta__";

export type DesignScopePageView = "scope" | "hierarchy";

export type DesignScopeViewLayout = {
  view: DesignScopePageView;
  treeSystem: CsiSystem;
};

export const DEFAULT_DESIGN_SCOPE_VIEW_LAYOUT: DesignScopeViewLayout = {
  view: "hierarchy",
  treeSystem: "UF"
};

function isPageView(value: unknown): value is DesignScopePageView {
  return value === "scope" || value === "hierarchy";
}

function isCsiSystem(value: unknown): value is CsiSystem {
  return value === "UF" || value === "MF";
}

export function parseDesignScopeViewLayout(
  raw: unknown
): DesignScopeViewLayout | null {
  if (!raw || typeof raw !== "object") return null;
  const { view, treeSystem } = raw as {
    view?: unknown;
    treeSystem?: unknown;
  };
  if (!isPageView(view) || !isCsiSystem(treeSystem)) return null;
  return { view, treeSystem };
}

export function readDesignScopeViewLayout(
  state: TSaveColumnState | null | undefined
): DesignScopeViewLayout | null {
  const columnList = state?.columnList;
  if (!columnList?.length) return null;
  const meta = columnList.find(
    (c) => c.field?.name === DESIGN_SCOPE_VIEW_META_FIELD_NAME
  );
  if (!meta?.field?.agFilter) return null;
  try {
    return parseDesignScopeViewLayout(JSON.parse(meta.field.agFilter));
  } catch {
    return null;
  }
}

function viewMetaColumn(
  layout: DesignScopeViewLayout,
  order: number
): TColumnType {
  return {
    order,
    visibility: false,
    field: {
      name: DESIGN_SCOPE_VIEW_META_FIELD_NAME,
      agFilter: JSON.stringify(layout),
      filter: null,
      sortOrder: null,
      range: null,
      width: null
    }
  };
}

/** Replace any existing view-meta row, then append a fresh one. */
export function withDesignScopeViewMeta(
  state: TSaveColumnState,
  layout: DesignScopeViewLayout
): TSaveColumnState {
  const withoutMeta = (state.columnList ?? []).filter(
    (c) => c.field?.name !== DESIGN_SCOPE_VIEW_META_FIELD_NAME
  );
  return {
    ...state,
    columnList: [...withoutMeta, viewMetaColumn(layout, withoutMeta.length + 1)]
  };
}
